import { useMutation, useQueryClient } from '@tanstack/react-query';

import { axiosPrivate } from 'app/utils';

import { markWebsiteAsChanged } from '../../../hooks/usePlusWebsites';
import type { PageSeo } from '../Pages/hooks/useGetPageDetails';
import { pagesQueryKey, type PagesResponse } from './useGetPages';

interface CreatePageParams {
  websiteId: string;
  name: string;
  href: string;
  seo: PageSeo;
}

function createPage({ websiteId, ...body }: CreatePageParams) {
  return axiosPrivate
    .post(`/v3/pharmacist/plus-websites/${websiteId}/pages`, body)
    .then((r) => r.data);
}

export function useCreatePage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPage,
    onSuccess: (data: { id?: string; page?: { id?: string } }, { websiteId, name, href }) => {
      const queryKey = pagesQueryKey(websiteId);
      markWebsiteAsChanged(queryClient, websiteId);
      const createdId = data?.id ?? data?.page?.id;

      // The preview pages endpoint lags behind this POST, so refetching here can
      // return a list without the new page and leave the tab looking unchanged
      // until a manual reload. Write the created page in directly instead.
      if (!createdId) {
        queryClient.invalidateQueries({ queryKey });
        return;
      }

      queryClient.setQueryData<PagesResponse>(queryKey, (old) =>
        old
          ? {
              ...old,
              pages: [...old.pages, { id: createdId, name, href }],
              pagination: { ...old.pagination, total: old.pagination.total + 1 },
            }
          : old
      );
    },
    meta: {
      successMessage: 'Page created successfully',
      errorMessage: 'Failed to create page',
    },
  });
}
